// src/App.js

import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom';
import { Alert, Layout, Spin, ConfigProvider } from 'antd';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { MechanicAuthProvider } from './modules/maintenance/contexts/MechanicAuthContext';

// --- (STATIC IMPORTS) ---
import Navbar from './components/Navbar';
import ProtectedRoute from './components/ProtectedRoute';
import SessionTimeoutWrapper from './components/AppLayout'; 

// --- (LAZY-LOADED PAGES) ---
const Login = lazy(() => import('./components/Login'));
const UserManagement = lazy(() => import('./components/UserManagement'));
const CompanySetup = lazy(() => import('./components/CompanySetupForm'));
const ManageCompanySetup = lazy(() => import('./components/ManageCompanySetup'));
const SalaryCalculator = lazy(() => import('./components/SalaryCalculatorForm'));
const ManageMonthlyCalculations = lazy(() => import('./components/ManageMonthlyCalculations'));
const LeadInfoForm = lazy(() => import('./components/LeadInfoForm'));
const ManageLeadSalary = lazy(() => import('./components/ManageLeadSalary'));
const RecorderInfoForm = lazy(() => import('./components/RecorderInfoForm'));
const ManageRecorderSalary = lazy(() => import('./components/ManageRecorderSalary'));
const ExcavatorInfoForm = lazy(() => import('./components/ExcavatorInfoForm'));
const ManageExcavatorSalary = lazy(() => import('./components/ManageExcavatorSalary'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const PageNotFound = lazy(() => import('./components/PageNotFound'));
const FleetCalculator = lazy(() => import('./modules/truck-operations/components/MonthlyDieselCalculator'));
const DailyOperationsForm = lazy(() => import('./modules/truck-operations/components/DailyOperationsForm'));
const RecordManagement = lazy(() => import('./modules/truck-operations/components/RecordManagement'));
const MonthlyOverview = lazy(() => import('./modules/truck-operations/components/MonthlyOverview'));
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));

// --- MECHANIC MODULE IMPORTS (LAZY) ---
const MechanicPinLogin = lazy(() => import('./modules/maintenance/components/MechanicPinLogin'));
const MechanicDashboard = lazy(() => import('./modules/maintenance/components/MechanicDashboard'));
const MechanicSetup = lazy(() => import('./components/MechanicSetup'));
const MechanicReportForm = lazy(() => import('./modules/maintenance/components/MechanicReportForm'));
const MechanicJobDetails = lazy(() => import('./modules/maintenance/components/MechanicJobDetails'));
const WeeklyInspectionForm = lazy(() => import('./modules/maintenance/components/WeeklyInspectionForm'));
const ScheduledServiceForm = lazy(() => import('./modules/maintenance/components/ScheduledServiceForm'));
// MechanicHistory import is removed

// --- ADMIN MAINTENANCE IMPORTS (LAZY) ---
const AdminMaintenanceLog = lazy(() => import('./modules/maintenance/pages/AdminMaintenanceLog'));
const AdminMaintenanceDashboard = lazy(() => import('./modules/maintenance/pages/AdminMaintenanceDashboard'));
const AdminReviewEdit = lazy(() => import('./modules/maintenance/pages/AdminReviewEdit'));
const MechanicManagement = lazy(() => import('./modules/maintenance/pages/MechanicManagement'));
const AdminInspectionLog = lazy(() => import('./modules/maintenance/pages/AdminInspectionLog'));
const AdminServiceLog = lazy(() => import('./modules/maintenance/pages/AdminServiceLog'));

// --- FLEET DASHBOARD (LAZY) ---
const FleetDashboard = lazy(() => import('./modules/fleet-dashboard'));


const { Content, Footer } = Layout;

// --- SessionMessage (Unchanged) ---
const SessionMessage = () => {
  const location = useLocation();
  const message = location.state?.message;
  if (!message) return null;
  return (
    <Alert
      message="Session Expired"
      description={message}
      type="info"
      showIcon
      closable
      style={{
        marginBottom: 24,
        borderRadius: 0,
        maxWidth: 800,
        margin: '1rem auto'
      }}
    />
  );
};

// --- ADMIN LAYOUT WRAPPER (Unchanged) ---
const AdminLayout = () => {
  const { loading } = useAuth(); 
  if (loading) {
    return <Spin size="large" fullscreen tip="Loading Admin..." />;
  }
  return (
    <SessionTimeoutWrapper>
      <Layout style={{ minHeight: '100vh' }}>
        <Navbar />
        <Content style={{ padding: '0 50px', marginTop: 64 }}>
          <Outlet />
        </Content>
        <Footer style={{ textAlign: 'center', background: '#F8F8F8', borderTop: '1px solid #E5E5E5' }}>
          Admin System ©{new Date().getFullYear()}
        </Footer>
      </Layout>
    </SessionTimeoutWrapper>
  );
};

// --- MECHANIC LAYOUT WRAPPER (Unchanged) ---
const MechanicLayout = () => {
  const mechanicTheme = {
    token: {
      colorPrimary: '#36493E', 
      colorInfo: '#36493E',
      colorBgContainer: '#FFFFFF',
      colorBgLayout: '#F9F8F6',
      fontFamily: 'sans-serif',
      borderRadius: 4,
    },
    components: {
      Button: { primaryShadow: 'none' },
      Layout: { bodyBg: '#F8F8F6', headerBg: '#36493E' }
    }
  };
  return (
    <ConfigProvider theme={mechanicTheme}>
      <Layout style={{ minHeight: '100vh' }}>
        <Content style={{ padding: '1rem', maxWidth: '800px', margin: '0 auto', width: "80%" }}>
          <Outlet />
        </Content>
      </Layout>
    </ConfigProvider>
  );
};

// --- GlobalPageLoader (Unchanged) ---
const GlobalPageLoader = () => (
  <Spin size="large" fullscreen tip="Loading Page..." />
);

function App() {
  return (
    <AuthProvider>
      <MechanicAuthProvider>
        <Router>
          <SessionMessage />
          <Suspense fallback={<GlobalPageLoader />}>
            <Routes>
              {/* 1. Public Admin Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              {/* 2. Public Mechanic App Routes */}
              <Route path="/mechanic" element={<MechanicLayout />}>
                <Route index element={<MechanicPinLogin />} />
                <Route path="dashboard" element={<MechanicDashboard />} />
                <Route path="report" element={<MechanicReportForm />} />
                <Route path="job/:jobId" element={<MechanicJobDetails />} />
                <Route path="weekly-inspection" element={<WeeklyInspectionForm />} />
                <Route path="scheduled-service" element={<ScheduledServiceForm />} />
                {/* The /mechanic/history route is now REMOVED */}
              </Route>

              {/* 3. Secure Admin App Routes */}
              <Route path="/" element={<AdminLayout />}>
                <Route index element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } />
                
                <Route path="/mechanic-setup" element={
                  <ProtectedRoute requiredModule="user-management">
                    <MechanicSetup />
                  </ProtectedRoute>
                } />
                
                <Route path="/mechanic-management" element={
                  <ProtectedRoute requiredModule="user-management">
                    <MechanicManagement />
                  </ProtectedRoute>
                } />

                {/* --- (ADMIN MAINTENANCE ROUTES) --- */}
                <Route path="/maintenance-dashboard" element={
                  <ProtectedRoute>
                    <AdminMaintenanceDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/maintenance-log" element={
                  <ProtectedRoute>
                    <AdminMaintenanceLog />
                  </ProtectedRoute>
                } />
                <Route path="/admin/review-edit/:jobId" element={
                  <ProtectedRoute>
                    <AdminReviewEdit />
                  </ProtectedRoute>
                } />
                <Route path="/inspection-log" element={
                  <ProtectedRoute>
                    <AdminInspectionLog />
                  </ProtectedRoute>
                } />
                <Route path="/service-log" element={
                  <ProtectedRoute>
                    <AdminServiceLog />
                  </ProtectedRoute>
                } />
                
                {/* ... (all your other admin routes) ... */}
                <Route path="/company-setup" element={
                  <ProtectedRoute requiredModule="company-setup">
                    <CompanySetup />
                  </ProtectedRoute>
                } />
                <Route path="/manage-company-setup" element={
                  <ProtectedRoute requiredModule="manage-company-setup">
                    <ManageCompanySetup />
                  </ProtectedRoute>
                } />
                <Route path="/salary-calculator" element={
                  <ProtectedRoute requiredModule="salary-calculator">
                    <SalaryCalculator />
                  </ProtectedRoute>
                } />
                <Route path="/manage-monthly-calculations" element={
                  <ProtectedRoute requiredModule="manage-monthly-calculations">
                    <ManageMonthlyCalculations />
                  </ProtectedRoute>
                } />
                <Route path="/lead-info-form" element={
                  <ProtectedRoute requiredModule="lead-info-form">
                    <LeadInfoForm />
                  </ProtectedRoute>
                } />
                <Route path="/manage-lead-salary" element={
                  <ProtectedRoute requiredModule="manage-lead-salary">
                    <ManageLeadSalary />
                  </ProtectedRoute>
                } />
                <Route path="/recorder-info-form" element={
                  <ProtectedRoute requiredModule="recorder-info-form">
                    <RecorderInfoForm />
                  </ProtectedRoute>
                } />
                <Route path="/manage-recorder-salary" element={
                  <ProtectedRoute requiredModule="manage-recorder-salary">
                    <ManageRecorderSalary />
                  </ProtectedRoute>
                } />
                <Route path="/excavator-info-form" element={
                  <ProtectedRoute requiredModule="excavator-info-form">
                    <ExcavatorInfoForm />
                  </ProtectedRoute>
                } />
                <Route path="/manage-excavator-salary" element={
                  <ProtectedRoute requiredModule="manage-excavator-salary">
                    <ManageExcavatorSalary />
                  </ProtectedRoute>
                } />
                <Route path="/truck-operations" element={
                  <ProtectedRoute requiredModule="truck-operations">
                    <DailyOperationsForm />
                  </ProtectedRoute>
                } />
                <Route path="/fleet-live" element={
                  <ProtectedRoute requiredModule="truck-operations">
                    <FleetDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/truck-operations/records" element={
                  <ProtectedRoute requiredModule="truck-operations/records">
                    <RecordManagement />
                  </ProtectedRoute>
                } />
                <Route path="/diesel-monthly" element={
                  <ProtectedRoute requiredModule="diesel-monthly">
                    < FleetCalculator />
                  </ProtectedRoute>
                } />
                <Route path="/truck-monthly" element={
                  <ProtectedRoute requiredModule="truck-monthly">
                    <MonthlyOverview />
                  </ProtectedRoute>
                } />
                <Route path="/user-management" element={
                  <ProtectedRoute requiredModule="user-management">
                    <UserManagement />
                  </ProtectedRoute>
                } />

              </Route>

              {/* 4. Catch-all route */}
              <Route path="*" element={<PageNotFound />} />
            </Routes>
          </Suspense>
        </Router>
      </MechanicAuthProvider>
    </AuthProvider>
  );
}

export default App;